
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { LoadingState } from "./LoadingState";
import { ErrorState } from "./ErrorState";
import { DeleteVideoDialog } from "./DeleteVideoDialog";
import { VideoHistoryCard } from "./VideoHistoryCard";

interface VideoHistory {
  id: string;
  video_id: string;
  video_title: string;
  video_thumbnail: string;
  created_at: string;
}

interface VideoHistoryListProps {
  compact?: boolean;
  onNavigate?: () => void;
}

export const VideoHistoryList = ({
  compact = false,
  onNavigate,
}: VideoHistoryListProps) => {
  const [deleteVideoId, setDeleteVideoId] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: videoHistory = [], isLoading, error } = useQuery({
    queryKey: ["videoHistory"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("video_history")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching video history:", error);
        throw error;
      }
      return data as VideoHistory[];
    },
  });

  const handleDeleteVideo = async (id: string) => {
    try {
      const { error } = await supabase
        .from("video_history")
        .delete()
        .eq("id", id);

      if (error) throw error;

      const { error: chatError } = await supabase
        .from("chat_history")
        .delete()
        .eq("video_id", videoHistory.find((v) => v.id === id)?.video_id);

      if (chatError) {
        console.error("Error deleting chat history:", chatError);
      }

      queryClient.invalidateQueries({ queryKey: ["videoHistory"] });
      setDeleteVideoId(null);

      toast({
        title: "Success",
        description: "Video removed from history",
      });
    } catch (error) {
      console.error("Error deleting video:", error);
      toast({
        title: "Error",
        description: "Failed to delete video",
        variant: "destructive",
      });
    }
  };

  const handleCardClick = async (video: VideoHistory) => {
    try {
      if (onNavigate) {
        const sidebarElement = document.querySelector('[data-state="open"]');
        if (sidebarElement) {
          sidebarElement.classList.add("animate-slide-out-right");
          setTimeout(() => {
            onNavigate();
          }, 300);
        } else {
          onNavigate();
        }
      }

      // Pre-fetch data and update cache
      const [transcriptResponse, chatResponse] = await Promise.all([
        supabase
          .from("video_transcripts")
          .select("transcript")
          .eq("video_id", video.video_id)
          .maybeSingle(),
        supabase
          .from("chat_history")
          .select("messages")
          .eq("video_id", video.video_id)
          .order('created_at', { ascending: false })
          .limit(1)
      ]);

      if (transcriptResponse.data) {
        queryClient.setQueryData(
          ["transcript", video.video_id],
          transcriptResponse.data
        );
      }

      if (chatResponse.data && chatResponse.data.length > 0) {
        queryClient.setQueryData(
          ["chatHistory", video.video_id],
          chatResponse.data[0]
        );
      }

      setTimeout(() => {
        navigate(`/workspace?video=${video.video_id}&showChat=true`);
      }, 150);
    } catch (error) {
      console.error("Error navigating to video:", error);
      toast({
        title: "Error",
        description: "Failed to load video chat. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState onRetry={() => queryClient.invalidateQueries({ queryKey: ["videoHistory"] })} />;

  return (
    <div className="space-y-2">
      {videoHistory.map((video) => (
        <VideoHistoryCard
          key={video.id}
          video={video}
          onDelete={setDeleteVideoId}
          onClick={handleCardClick}
        />
      ))}

      <DeleteVideoDialog
        open={!!deleteVideoId}
        onOpenChange={(open) => !open && setDeleteVideoId(null)}
        onConfirm={() => deleteVideoId && handleDeleteVideo(deleteVideoId)}
      />
    </div>
  );
};
